const FAIZ_DISCOUNT_DISMISSED_KEY = 'faizDiscountDismissed';
const FAIZ_DISCOUNT_APPLIED_KEY = 'faizDiscountApplied';
const FAIZ_DISCOUNT_CODE = 'FAIZ10';

class FaizDiscountPopup {
  constructor(root) {
    this.root = root;
    this.dialog = root.querySelector('[data-faiz-discount-dialog]');
    this.trigger = root.querySelector('[data-faiz-discount-trigger]');
    this.closeButton = root.querySelector('[data-faiz-discount-close]');
    this.skipButton = root.querySelector('[data-faiz-discount-skip]');
    this.form = root.querySelector('[data-faiz-discount-form]');
    this.formError = root.querySelector('[data-faiz-discount-error]');
    this.impact = root.querySelector('[data-faiz-discount-impact]');
    this.successMessage = root.querySelector('.faiz-discount-popup__message--success');
    this.stickyCart = document.querySelector('.hcp-sticky-cart');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.compactViewport = window.matchMedia('(max-width: 749px)');
    this.openTimer = null;
    this.closeTimer = null;
    this.scrollIdleTimer = null;
    this.retractTimer = null;
    this.applied = false;
    this.scrollTriggerEnabled = false;
    this.lastScrollTop = 0;
    this.boundHandleScroll = () => this.handleScroll();
    this.boundRefreshScrollTarget = () => this.bindScrollTarget();
    this.scrollTarget = null;
    this.lastTrigger = null;
  }

  connect() {
    if (!this.dialog || !this.trigger || !this.closeButton) return;

    this.bindScrollTarget();
    window.addEventListener('resize', this.boundRefreshScrollTarget, { passive: true });
    this.trigger.addEventListener('click', () => this.open('trigger'));
    this.closeButton.addEventListener('click', () => this.close());
    this.skipButton?.addEventListener('click', () => this.close());
    this.form?.addEventListener('submit', (event) => this.handleSignup(event));
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.observeStickyCart();

    if (this.wasApplied()) {
      this.restoreAppliedState();
      this.applyDiscountToCart();
      return;
    }

    if (this.successMessage && !this.successMessage.hidden) {
      this.writeApplied();
      this.finishClose();
      this.showAppliedState();
      this.applyDiscountToCart();
      return;
    }

    if (this.wasDismissed() || document.documentElement.classList.contains('shopify-design-mode')) {
      this.scrollTriggerEnabled = true;
      this.hideTrigger(true);
      return;
    }

    if (this.compactViewport.matches) {
      this.scrollTriggerEnabled = true;
      this.revealTrigger();
      return;
    }

    this.openTimer = window.setTimeout(() => this.open('automatic'), 1600);
  }

  async handleSignup(event) {
    event.preventDefault();
    if (!this.form || this.form.dataset.submitting === 'true') return;
    if (!this.form.reportValidity()) return;

    this.form.dataset.submitting = 'true';
    this.formError && (this.formError.hidden = true);
    const submit = this.form.querySelector('button[type="submit"]');
    if (submit) submit.disabled = true;

    // The offer is a storefront promise, so activate it immediately. Shopify's
    // newsletter endpoint can reject background requests (for example when its
    // bot protection is active); that must never navigate the customer away or
    // prevent the promised discount from appearing in cart and checkout.
    this.writeApplied();
    this.finishClose();
    this.showAppliedState();
    void this.applyDiscountToCart();

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Newsletter signup failed');
    } catch {
      // Keep the discount active. Newsletter capture is best-effort and must
      // not turn a successful offer activation into a customer-facing error.
    } finally {
      delete this.form.dataset.submitting;
      if (submit) submit.disabled = false;
    }
  }

  showAppliedState() {
    this.writeDismissed();
    this.applied = true;
    this.scrollTriggerEnabled = false;
    this.hideTrigger(true);
    this.root.classList.add('is-applied');

    if (this.impact && !this.prefersReducedMotion.matches) {
      this.impact.hidden = false;
      this.root.classList.remove('is-celebrating');
      void this.impact.offsetWidth;
      this.root.classList.add('is-celebrating');
      window.setTimeout(() => {
        this.root.classList.remove('is-celebrating');
        this.impact.hidden = true;
      }, 1500);
    }
  }

  restoreAppliedState() {
    this.writeDismissed();
    this.applied = true;
    this.scrollTriggerEnabled = false;
    this.root.classList.add('is-applied');
    this.hideTrigger(true);
  }

  async applyDiscountToCart() {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: FAIZ_DISCOUNT_CODE }),
      });
      if (!response.ok) return;
      document.dispatchEvent(new CustomEvent('shopify:cart:discount-update', {
        bubbles: true,
        detail: { discountCode: FAIZ_DISCOUNT_CODE },
      }));
    } catch {
      return;
    }
  }

  getScrollTarget() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (
      pageWrapper
      && pageWrapper.scrollHeight > pageWrapper.clientHeight + 2
    ) {
      return pageWrapper;
    }

    return window;
  }

  bindScrollTarget() {
    const nextTarget = this.getScrollTarget();
    if (this.scrollTarget === nextTarget) return;

    this.scrollTarget?.removeEventListener('scroll', this.boundHandleScroll);
    nextTarget.addEventListener('scroll', this.boundHandleScroll, { passive: true });
    this.scrollTarget = nextTarget;
    this.lastScrollTop = this.getScrollTop();
  }

  open(source) {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.clearTriggerTimers();
    this.scrollTriggerEnabled = false;
    this.hideTrigger(true);
    this.lastTrigger = source === 'trigger' ? this.trigger : document.activeElement;
    this.dialog.classList.remove('is-collapsing');
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (!this.dialog.open) return;
    this.writeDismissed();
    window.clearTimeout(this.openTimer);

    if (this.prefersReducedMotion.matches) {
      this.finishClose();
      return;
    }

    this.dialog.classList.add('is-collapsing');
    this.closeTimer = window.setTimeout(() => this.finishClose(), 440);
  }

  finishClose() {
    if (this.dialog.open) this.dialog.close();
    this.dialog.classList.remove('is-collapsing');
    this.scrollTriggerEnabled = !this.applied;
    this.hideTrigger(true);
  }

  handleScroll() {
    if (!this.scrollTriggerEnabled || this.dialog.open) return;

    const currentScrollTop = this.getScrollTop();
    const maximumScrollTop = this.getMaximumScrollTop();
    const isAtBottom = maximumScrollTop > 0 && currentScrollTop >= maximumScrollTop - 2;
    this.lastScrollTop = currentScrollTop;

    if (this.isHomepageFirstSectionVisible()) {
      this.hideTrigger(true);
      return;
    }

    if (isAtBottom) {
      this.hideTrigger(true);
      return;
    }

    if (this.trigger.hidden || this.trigger.classList.contains('is-retracting')) {
      this.revealTrigger();
    }
    this.scheduleTriggerHide();
  }

  isHomepageFirstSectionVisible() {
    const firstSection = document.querySelector('.hc-home > section:first-child');
    return Boolean(firstSection && firstSection.getBoundingClientRect().bottom > 1);
  }

  getScrollTop() {
    if (this.scrollTarget === window) {
      return window.scrollY || document.scrollingElement?.scrollTop || 0;
    }

    return this.scrollTarget?.scrollTop || 0;
  }

  getMaximumScrollTop() {
    if (this.scrollTarget === window) {
      const scrollingElement = document.scrollingElement;
      return Math.max(0, (scrollingElement?.scrollHeight || 0) - window.innerHeight);
    }

    return Math.max(0, (this.scrollTarget?.scrollHeight || 0) - (this.scrollTarget?.clientHeight || 0));
  }

  clearTriggerTimers() {
    window.clearTimeout(this.scrollIdleTimer);
    window.clearTimeout(this.retractTimer);
    this.scrollIdleTimer = null;
    this.retractTimer = null;
  }

  hideTrigger(immediate = false) {
    this.clearTriggerTimers();
    this.trigger.classList.remove('is-attracting');

    if (immediate || this.prefersReducedMotion.matches) {
      this.trigger.classList.remove('is-retracting');
      this.trigger.hidden = true;
      return;
    }

    this.trigger.classList.add('is-retracting');
    this.retractTimer = window.setTimeout(() => {
      this.trigger.hidden = true;
      this.trigger.classList.remove('is-retracting');
    }, 280);
  }

  scheduleTriggerHide() {
    this.clearTriggerTimers();
    // The entrance uses a 920 ms spring. Keep the capsule stable long enough
    // to be clicked after it lands instead of retracting mid-animation.
    this.scrollIdleTimer = window.setTimeout(() => this.hideTrigger(), 1500);
  }

  revealTrigger() {
    this.clearTriggerTimers();
    this.trigger.hidden = false;
    this.trigger.classList.remove('is-retracting');
    this.trigger.classList.remove('is-attracting');
    void this.trigger.offsetWidth;
    this.trigger.classList.add('is-attracting');
  }

  observeStickyCart() {
    if (!this.stickyCart) return;

    const sync = () => {
      this.root.classList.toggle('is-sticky-cart-visible', this.stickyCart.classList.contains('is-visible'));
    };

    sync();
    this.stickyObserver = new MutationObserver(sync);
    this.stickyObserver.observe(this.stickyCart, { attributes: true, attributeFilter: ['class'] });
  }

  wasDismissed() {
    try {
      return sessionStorage.getItem(FAIZ_DISCOUNT_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  writeDismissed() {
    try {
      sessionStorage.setItem(FAIZ_DISCOUNT_DISMISSED_KEY, 'true');
    } catch {
      return;
    }
  }

  wasApplied() {
    try {
      return localStorage.getItem(FAIZ_DISCOUNT_APPLIED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  writeApplied() {
    this.applied = true;
    try {
      localStorage.setItem(FAIZ_DISCOUNT_APPLIED_KEY, 'true');
    } catch {
      return;
    }
  }

  disconnect() {
    this.scrollTarget?.removeEventListener('scroll', this.boundHandleScroll);
    window.removeEventListener('resize', this.boundRefreshScrollTarget);
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    this.clearTriggerTimers();
    this.stickyObserver?.disconnect();
  }
}

const initFaizDiscountPopup = () => {
  document.querySelectorAll('[data-faiz-discount-popup]').forEach((root) => {
    root.faizDiscountPopup?.disconnect?.();
    const popup = new FaizDiscountPopup(root);
    root.faizDiscountPopup = popup;
    popup.connect();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFaizDiscountPopup, { once: true });
} else {
  initFaizDiscountPopup();
}
